import { supabase } from '../supabase/client';

/**
 * Account Status Enumeration
 * Represents all possible account states in the system
 */
export enum AccountStatus {
    ACTIVE = 'active',
    PENDING_EMAIL_VERIFICATION = 'pending_email_verification',
    PENDING_PARTNER_VERIFICATION = 'pending_partner_verification',
    PROFILE_INCOMPLETE = 'profile_incomplete',
    SUSPENDED = 'suspended',
    BANNED = 'banned',
    REJECTED = 'rejected',
}

/**
 * User Type Enumeration
 */
export enum UserType {
    PARTNER = 'partner',
    REGULAR = 'regular',
}

/**
 * Account Status Check Result
 */
export interface AccountStatusCheck {
    status: AccountStatus;
    isAllowed: boolean;
    redirectTo?: string;
    message?: string;
}

/**
 * Partner Status Data from Database
 */
export interface PartnerStatusData {
    status: 'pending_email_verification' | 'pending_verification' | 'verified' | 'rejected' | 'suspended';
    verified_at: string | null;
    profile_completed?: boolean;
}

/**
 * User Status Data from Database
 */
export interface UserStatusData {
    user_status: string | null;
    user_type: 'partner' | 'regular';
}

/**
 * Check partner account status
 * @param userId - The user's auth ID
 * @returns Account status check result
 */
export async function checkPartnerAccountStatus(
    userId: string
): Promise<AccountStatusCheck> {
    try {
        // Fetch partner data
        const { data: partner, error } = await supabase
            .from('partners')
            .select('status, verified_at, profile_completed')
            .eq('user_id', userId)
            .single();

        if (error || !partner) {
            const { data: profile } = await supabase
                .from('user_profile')
                .select('user_type')
                .eq('user_id', userId)
                .maybeSingle();

            if (profile?.user_type === 'partner') {
                return {
                    status: AccountStatus.PROFILE_INCOMPLETE,
                    isAllowed: false,
                    redirectTo: '/partner/complete-profile',
                    message: 'Please complete your partner profile to continue.',
                };
            }

            return {
                status: AccountStatus.ACTIVE,
                isAllowed: true,
            };
        }

        const partnerData = partner as unknown as PartnerStatusData;

        // Check email verification from auth.users
        const { data: { user } } = await supabase.auth.getUser();

        if (user && !user.email_confirmed_at) {
            return {
                status: AccountStatus.PENDING_EMAIL_VERIFICATION,
                isAllowed: false,
                redirectTo: '/partner/verify-email',
                message: 'Please verify your email address to continue.',
            };
        }

        // Phase 2 must run after email confirm and before admin review.
        // Only an explicit true counts — the old column default was true.
        if (partnerData.profile_completed !== true) {
            return {
                status: AccountStatus.PROFILE_INCOMPLETE,
                isAllowed: false,
                redirectTo: '/partner/complete-profile',
                message: 'Please complete your partner profile to continue.',
            };
        }

        // Email is confirmed and Phase 2 is done. Admin review is next.
        if (
            partnerData.status === 'pending_verification' ||
            partnerData.status === 'pending_email_verification'
        ) {
            return {
                status: AccountStatus.PENDING_PARTNER_VERIFICATION,
                isAllowed: false,
                redirectTo: '/partner/verification-pending',
                message: 'Your partner account is pending verification by our team.',
            };
        }

        if (partnerData.status === 'rejected') {
            return {
                status: AccountStatus.REJECTED,
                isAllowed: false,
                redirectTo: '/partner/verification-rejected',
                message: 'Your partner application was not approved.',
            };
        }

        if (partnerData.status === 'suspended') {
            return {
                status: AccountStatus.SUSPENDED,
                isAllowed: false,
                redirectTo: '/account-suspended',
                message: 'Your account has been suspended.',
            };
        }

        // All checks passed
        return {
            status: AccountStatus.ACTIVE,
            isAllowed: true,
        };
    } catch (error) {

        // Fail open for now to prevent blocking legitimate users
        return {
            status: AccountStatus.ACTIVE,
            isAllowed: true,
        };
    }
}

/**
 * Check regular user account status
 * @param userId - The user's auth ID
 * @returns Account status check result
 */
export async function checkRegularUserAccountStatus(
    userId: string
): Promise<AccountStatusCheck> {
    try {
        // Fetch user profile data
        const { data: profile, error } = await supabase
            .from('user_profile')
            .select('user_status')
            .eq('user_id', userId)
            .single();

        if (error || !profile) {

            return {
                status: AccountStatus.ACTIVE,
                isAllowed: true,
            };
        }

        const userData = profile as UserStatusData;

        // Check if account is suspended
        if (userData.user_status === 'suspended') {
            return {
                status: AccountStatus.SUSPENDED,
                isAllowed: false,
                redirectTo: '/account-suspended',
                message: 'Your account has been suspended.',
            };
        }

        // Check if account is banned
        if (userData.user_status === 'banned') {
            return {
                status: AccountStatus.BANNED,
                isAllowed: false,
                redirectTo: '/account-suspended',
                message: 'Your account has been banned.',
            };
        }

        // Check email or phone verification from auth.users
        const { data: { user } } = await supabase.auth.getUser();

        // For regular users, either email OR phone verification is acceptable
        if (user && !user.email_confirmed_at && !user.phone_confirmed_at) {
            return {
                status: AccountStatus.PENDING_EMAIL_VERIFICATION,
                isAllowed: false,
                redirectTo: '/verify-email',
                message: 'Please verify your email or phone to continue.',
            };
        }

        // All checks passed
        return {
            status: AccountStatus.ACTIVE,
            isAllowed: true,
        };
    } catch (error) {

        // Fail open for now to prevent blocking legitimate users
        return {
            status: AccountStatus.ACTIVE,
            isAllowed: true,
        };
    }
}

/**
 * Single post-auth landing path for partners and regular users.
 * Prefer the status check's redirect; otherwise use the default for that status.
 */
export function resolvePostAuthPath(
    check: AccountStatusCheck,
    userType: UserType
): string {
    if (check.redirectTo) return check.redirectTo;
    return getAccountStatusRedirect(check.status, userType);
}

export function getAccountStatusRedirect(
    status: AccountStatus,
    userType: UserType
): string {
    switch (status) {
        case AccountStatus.PENDING_EMAIL_VERIFICATION:
            return userType === UserType.PARTNER ? '/partner/verify-email' : '/verify-email';

        case AccountStatus.PENDING_PARTNER_VERIFICATION:
            return '/partner/verification-pending';

        case AccountStatus.PROFILE_INCOMPLETE:
            return '/partner/complete-profile';

        case AccountStatus.REJECTED:
            return '/partner/verification-rejected';

        case AccountStatus.SUSPENDED:
        case AccountStatus.BANNED:
            return '/account-suspended';

        case AccountStatus.ACTIVE:
        default:
            return userType === UserType.PARTNER ? '/partner/dashboard' : '/home';
    }
}

/**
 * Check account status based on user type
 * @param userId - The user's auth ID
 * @param userType - The user type
 * @returns Account status check result
 */
export async function checkAccountStatus(
    userId: string,
    userType: UserType
): Promise<AccountStatusCheck> {
    if (userType === UserType.PARTNER) {
        return checkPartnerAccountStatus(userId);
    } else {
        return checkRegularUserAccountStatus(userId);
    }
}

/**
 * Get user type from profile
 * @param userId - The user's auth ID
 * @returns User type or null
 */
export async function getUserType(userId: string): Promise<UserType | null> {
    try {
        const { data: profile, error } = await supabase
            .from('user_profile')
            .select('user_type')
            .eq('user_id', userId)
            .single();

        if (error || !profile) {
            return null;
        }

        return profile.user_type === 'partner' ? UserType.PARTNER : UserType.REGULAR;
    } catch (error) {

        return null;
    }
}
